import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, MessagesSquare } from "lucide-react";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime } from "../../../lib/format";
import { ChatThread } from "../../../components/common/ChatThread";
import { CustomerSelect } from "./CustomerSelect";

const ThreadRow = ({ th, active, onOpen }) => (
  <button type="button" onClick={() => onOpen(th.customer_code)} data-testid={`thread-${th.customer_code}`}
    className={`flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors ${active ? "bg-navy text-white" : "hover:bg-canvas"}`}>
    <div className="min-w-0 flex-1">
      <p className={`truncate text-sm font-semibold ${active ? "text-white" : "text-navy"}`}>{th.legal_name || th.name}</p>
      <p className={`truncate text-xs ${active ? "text-white/70" : "text-slate-500"}`}>{th.name} · {th.last_text}</p>
      <p className={`text-[10px] ${active ? "text-white/60" : "text-slate-400"}`}>{fmtDateTime(th.last_at)}</p>
    </div>
    {th.unread > 0 && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1 text-[11px] font-bold text-navy" data-testid={`thread-unread-${th.customer_code}`}>{th.unread}</span>}
  </button>
);

export default function Messages() {
  const { t } = useI18n();
  const [sp, setSp] = useSearchParams();
  const code = sp.get("c");
  const [threads, setThreads] = useState([]);
  const load = useCallback(() => {
    api.get("/staff/chat").then((r) => setThreads(r.data)).catch(() => {});
    window.dispatchEvent(new Event("vavlis:chat"));
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);
  const open = (c) => setSp(c ? { c } : {});
  const current = threads.find((x) => x.customer_code === code);

  return (
    <div className="space-y-4" data-testid="messages-page">
      <h1 className="text-3xl font-bold text-navy sm:text-4xl">{t("x.chat.inbox")}</h1>
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <aside className={`space-y-3 ${code ? "hidden lg:block" : ""}`}>
          <CustomerSelect onPick={open} />
          <div className="max-h-[65vh] space-y-1 overflow-y-auto rounded-2xl bg-white p-2 shadow-sm" data-testid="thread-list">
            {threads.length === 0 && <p className="flex items-center gap-2 p-4 text-sm text-slate-500"><MessagesSquare className="h-4 w-4" />{t("x.chat.noThreads")}</p>}
            {threads.map((th) => <ThreadRow key={th.customer_code} th={th} active={th.customer_code === code} onOpen={open} />)}
          </div>
        </aside>
        {code ? (
          <section className="flex h-[calc(100dvh-14rem)] min-h-[420px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => open(null)} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white lg:hidden" aria-label={t("shop.back")} data-testid="thread-back"><ArrowLeft className="h-5 w-5" /></button>
              <div className="min-w-0"><p className="truncate font-semibold text-navy" data-testid="thread-title">{current?.legal_name || current?.name || code}</p>{current && <p className="truncate text-xs text-slate-500">{current.name} · {code}</p>}</div>
            </div>
            <div className="min-h-0 flex-1"><ChatThread key={code} url={`/staff/chat/${encodeURIComponent(code)}`} side="staff" onRead={load} /></div>
          </section>
        ) : <p className="hidden rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500 lg:block">{t("x.chat.pick")}</p>}
      </div>
    </div>
  );
}
